import { Injectable } from '@angular/core';

import { createSelector, Store } from '@ngrx/store';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserIdMap } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import { DepartmentSelectors } from '@recruitee/department-data';
import { memoizeSelector } from '@recruitee/ngrx';
import {
  AssignedFlowsSelectors,
  AvailableFlowsSelectors,
  RequisitionsSelectors,
} from '@recruitee/requisitions-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';
import type {
  Requisition,
  RequisitionAssignedFlow,
  RequisitionAvailableFlow,
} from '@recruitee/requisitions-types';

import { RootPageDataService } from './root-page-data.service';

const departmentNameIfModeEnabled = memoizeSelector((requisitionId: number) =>
  createSelector(
    RequisitionsConfigSelectors.isDepartmentsMode,
    RequisitionsSelectors.byId(requisitionId),
    DepartmentSelectors.entities(),
    (isDepartmentsMode, requisition, departments) => {
      if (!isDepartmentsMode || !requisition?.departmentId) return undefined;
      return departments.find(dep => dep.id === requisition.departmentId)?.name;
    },
  ),
);

const isLoaded = memoizeSelector((requisitionId: number) =>
  createSelector(
    AssignedFlowsSelectors.isLoaded({ requisitionId }),
    AvailableFlowsSelectors.isLoaded(requisitionId),
    (a, b) => a && b,
  ),
);

@Injectable()
export class FlowPageDataService {
  public requisition$: Observable<Requisition>;
  public approvalFlow$: Observable<RequisitionAssignedFlow | null>;
  public adminsMap$: Observable<UserIdMap>;
  public flowsList$: Observable<RequisitionAvailableFlow[]>;
  public isDepartmentsMode$: Observable<boolean>;
  public departmentName$: Observable<string | undefined>;
  public isLoaded$: Observable<boolean>;

  constructor(
    private rootData: RootPageDataService,
    private store: Store,
  ) {
    this.requisition$ = rootData.requisition$;
    this.approvalFlow$ = this.requisition$.pipe(
      switchMap(requisition =>
        store.select(AssignedFlowsSelectors.byRequisitionId({ requisitionId: requisition.id })),
      ),
    );
    this.adminsMap$ = rootData.adminsMap$;
    this.flowsList$ = this.requisition$.pipe(
      switchMap(requisition =>
        store.select(AvailableFlowsSelectors.byId(requisition.id)).pipe(
          filterBoolean,
          map(entity => entity.approvalFlows),
        ),
      ),
    );
    this.isDepartmentsMode$ = this.store.select(RequisitionsConfigSelectors.isDepartmentsMode);
    this.departmentName$ = this.requisition$.pipe(
      switchMap(requisition => store.select(departmentNameIfModeEnabled(requisition.id))),
    );
    this.isLoaded$ = this.requisition$.pipe(
      switchMap(requisition => store.select(isLoaded(requisition.id))),
    );
  }
}
